/* Markdown editor toolbar component with formatting buttons
   Features: Bold, italic, underline, headings, lists, links, and more
   Inserts markdown/HTML at cursor position or wraps selected text */


import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Bold,
  Italic,
  Underline,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  List,
  ListOrdered,
  Link,
  Image,
  Code,
  Quote,
  Table,
  MinusSquare,
} from 'lucide-react';

interface MarkdownToolbarProps {
  onInsert: (before: string, after?: string) => void;
}

export default function MarkdownToolbar({ onInsert }: MarkdownToolbarProps) {
  return (
    <div className="flex gap-1 flex-wrap p-3 bg-gray-50 rounded-lg border border-gray-200">
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Bold (Ctrl+B)"
        onClick={() => onInsert('**', '**')}
      >
        <Bold className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Italic (Ctrl+I)"
        onClick={() => onInsert('*', '*')}
      >
        <Italic className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Underline"
        onClick={() => onInsert('<u>', '</u>')}
      >
        <Underline className="w-4 h-4" />
      </Button>

      <Separator orientation="vertical" className="h-8 mx-1" />

      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Heading 1"
        onClick={() => onInsert('\n# ', '\n')}
      >
        <Heading1 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Heading 2"
        onClick={() => onInsert('\n## ', '\n')}
      >
        <Heading2 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Heading 3"
        onClick={() => onInsert('\n### ', '\n')}
      >
        <Heading3 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Heading 4"
        onClick={() => onInsert('\n#### ', '\n')}
      >
        <Heading4 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Heading 5"
        onClick={() => onInsert('\n##### ', '\n')}
      >
        <Heading5 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Heading 6"
        onClick={() => onInsert('\n###### ', '\n')}
      >
        <Heading6 className="w-4 h-4" />
      </Button>

      <Separator orientation="vertical" className="h-8 mx-1" />

      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Bullet List"
        onClick={() => onInsert('\n- ', '')}
      >
        <List className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Numbered List"
        onClick={() => onInsert('\n1. ', '')}
      >
        <ListOrdered className="w-4 h-4" />
      </Button>

      <Separator orientation="vertical" className="h-8 mx-1" />

      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Link"
        onClick={() => onInsert('[', '](https://)')}
      >
        <Link className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Image"
        onClick={() => onInsert('![', '](https://)')}
      >
        <img src="/assets/icon.webp" alt="Afbeelding invoegen" className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Code"
        onClick={() => onInsert('`', '`')}
      >
        <Code className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Quote"
        onClick={() => onInsert('\n> ', '')}
      >
        <Quote className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Horizontal Rule"
        onClick={() => onInsert('\n---\n', '')}
      >
        <MinusSquare className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        title="Table"
        onClick={() => onInsert('\n| Header 1 | Header 2 |\n|----------|----------|\n| Cell 1   | Cell 2   |\n', '')}
      >
        <Table className="w-4 h-4" />
      </Button>
    </div>
  );
}
